<script setup lang="ts">
import { IBasicSection } from '@/interface/IBasicSection'
import { ref } from 'vue'
import { useWindow } from '@/composables/useWindow'
import useInterval from '@/composables/useInterval'
import { chunkArray } from '@/utils/chunkArray'

const screen = useWindow()
const footerVue = ref<HTMLElement>()
const props = defineProps<{ footer: IBasicSection[] }>()
const sliderActive = ref(props.footer.length > screen.value.state)
const footerCards = ref(chunkArray(props.footer, screen.value.state))
const page = ref(0)
const nextPage = () => {
  footerVue.value?.classList.add('right-out')
  setTimeout(() => {
    footerVue.value?.classList.remove('right-out')
    if (page.value < footerCards.value.length) page.value++
    if (page.value === footerCards.value.length) page.value = 0
    footerVue.value?.classList.add('right-in')
  }, 150)

  setTimeout(() => {
    footerVue.value?.classList.remove('right-in')
  }, 500)
}

const previousPage = () => {
  footerVue.value?.classList.add('left-out')
  setTimeout(() => {
    footerVue.value?.classList.remove('left-out')
    if (page.value >= 0) page.value--
    if (page.value === -1) page.value = footerCards.value.length - 1
    footerVue.value?.classList.add('left-in')
  }, 150)

  setTimeout(() => {
    footerVue.value?.classList.remove('left-in')
  }, 500)
}

const gridColumns = () => {
  if (footerVue.value) {
    const counter = Math.min(props.footer.length, screen.value.state)
    footerVue.value.style.gridTemplateColumns = `repeat(${counter}, 1fr)`
  }
}

const interval = useInterval(nextPage, 4000)
const nextClick = () => {
  nextPage()
  interval.restart()
}
const previousClick = () => {
  previousPage()
  interval.restart()
}

onMounted(() => {
  gridColumns()
  if (sliderActive.value) interval.start()
  watch(
    () => sliderActive.value,
    () => {
      if (sliderActive.value) interval.start()
      if (!sliderActive.value) {
        interval.stop()
        page.value = 0
      }
    }
  )
  window.addEventListener('resize', () => {
    gridColumns()
    const counter = Math.min(props.footer.length, screen.value.state)
    sliderActive.value = props.footer.length > counter
    footerCards.value = chunkArray(props.footer, counter)
  })
})
</script>

<template>
  <div ref="footerVue" class="grid gap-6 relative grid-cols-5">
    <ElCard
      v-for="element in footerCards[page]"
      :key="element.title + screen.state + page"
      class="clear-el-card"
      :body-style="{ padding: '0' }"
      shadow="never"
    >
      <div class="min-h-[85px]">
        <p class="text-sm font-bold color-title-dark mb-4">
          {{ element.title }}
        </p>
        <p v-if="element.copy" class="font-medium">
          {{ element.copy }}
        </p>
      </div>
    </ElCard>
    <div class="cursor-pointer slider slider--left" v-if="sliderActive">
      <v-icon
        name="bi-arrow-right"
        scale="3"
        @click="previousClick"
        class="color-title-dark"
      />
    </div>
    <div class="cursor-pointer slider slider--right" v-if="sliderActive">
      <v-icon
        name="bi-arrow-right"
        scale="3"
        @click="nextClick"
        class="color-title-dark"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.clear-el-card {
  border: none;
  background: transparent;
  box-shadow: none !important;
}

.slider {
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  height: 100%;
  min-width: 40px;
  padding: 0 5px;
  transition: background-color 0.5s ease;

  &:hover {
    background-color: rgb(0, 0, 0, 0.1);

    svg {
      display: block;
    }
  }

  &--left {
    left: 0;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }

  &--right {
    right: 0;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  svg {
    display: none;
  }

  .left-out {
    -webkit-animation: swipe-center-left 0.25s infinite;
    animation: swipe-center-left 0.25s infinite;
    animation-iteration-count: 1;
  }

  .left-in {
    -webkit-animation: swipe-right-center 0.25s infinite;
    animation: swipe-right-center 0.25s infinite;
    animation-iteration-count: 1;
  }

  .right-out {
    -webkit-animation: swipe-center-right 0.25s infinite;
    animation: swipe-center-right 0.25s infinite;
    animation-iteration-count: 1;
  }

  .right-in {
    -webkit-animation: swipe-left-center 0.25s infinite;
    animation: swipe-left-center 0.25s infinite;
    animation-iteration-count: 1;
  }

  @keyframes swipe-center-left {
    0% {
      transform: translateX(0);
      opacity: 1;
    }

    100% {
      transform: translateX(-100%);
      opacity: 0;
    }
  }

  @keyframes swipe-right-center {
    0% {
      transform: translateX(+100%);
      opacity: 0;
    }

    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes swipe-center-right {
    0% {
      transform: translateX(0);
      opacity: 1;
    }

    100% {
      transform: translateX(+100%);
      opacity: 0;
    }
  }

  @keyframes swipe-left-center {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }

    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
}
</style>
